import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import {
  ChangeEvent,
  ClipboardEvent,
  CompositionEvent,
  FocusEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'

import { DIGITS_ONLY, WHITESPACE } from '../utils/b2b.constants'

import styles from './NumericQtyInput.module.scss'

const convertToDisplayLabel = (num: number, floor: number, showEmpty: boolean): string => {
  const floored = Math.max(floor, Number.isFinite(num) ? Math.trunc(num) : floor)
  return showEmpty && floored === 0 ? '' : String(floored)
}

export interface NumericQtyInputValidation {
  exceeded: boolean
  inventoryViolation: boolean
}

export interface NumericQtyInputProps {
  value: number
  onChange: (newValue: number) => void
  min?: number
  max?: number
  disabled?: boolean
  variant?: 'default' | 'grid'
  emptyWhenZero?: boolean
  className?: string
  ariaLabel?: string
  hasError?: boolean
  isOutOfStock?: boolean
  isOosActive?: boolean
  inventoryAvailable?: number
  onOosClick?: () => void
  onOosBlur?: () => void
  onValidationChange?: (validation: NumericQtyInputValidation) => void
}

/** Numeric-only input for quantity editing. No auto-clamping — emits raw values so callers handle validation. */
export const NumericQtyInput = ({
  value,
  onChange,
  min = 0,
  max,
  disabled = false,
  variant = 'default',
  emptyWhenZero = false,
  className,
  ariaLabel,
  hasError = false,
  isOutOfStock = false,
  isOosActive = false,
  inventoryAvailable,
  onOosClick,
  onOosBlur,
  onValidationChange,
}: NumericQtyInputProps) => {
  const { t } = useTranslation()
  const safeMin = Number.isFinite(min) ? Math.trunc(min) : 0
  const isEffectivelyDisabled = disabled || isOutOfStock

  const [inputValue, setInputValue] = useState(() => convertToDisplayLabel(value, safeMin, emptyWhenZero))
  const [isFocused, setIsFocused] = useState(false)
  const [isComposing, setIsComposing] = useState(false)
  const lastEmittedRef = useRef(value)
  const prevValidationRef = useRef<NumericQtyInputValidation>({ exceeded: false, inventoryViolation: false })
  const onValidationChangeRef = useRef(onValidationChange)
  onValidationChangeRef.current = onValidationChange

  // Compute validation state from current value
  const exceeded = max !== undefined && Number.isFinite(max) && value > max
  const inventoryViolation =
    !isOutOfStock &&
    !disabled &&
    inventoryAvailable !== undefined &&
    Number.isFinite(inventoryAvailable) &&
    value > inventoryAvailable

  // Report validation changes to parent (callback ref avoids re-runs from unstable closures)
  useEffect(() => {
    const prev = prevValidationRef.current
    if (prev.exceeded !== exceeded || prev.inventoryViolation !== inventoryViolation) {
      prevValidationRef.current = { exceeded, inventoryViolation }
      onValidationChangeRef.current?.({ exceeded, inventoryViolation })
    }
  }, [exceeded, inventoryViolation])

  // Derive visual error: external hasError OR internal validation failures
  const derivedHasError = hasError || exceeded || inventoryViolation

  useEffect(() => {
    lastEmittedRef.current = value
  }, [value])

  useEffect(() => {
    if (!isFocused) setInputValue(convertToDisplayLabel(value, safeMin, emptyWhenZero))
  }, [value, safeMin, isFocused, emptyWhenZero])

  const emit = (num: number) => {
    if (![value, lastEmittedRef.current].includes(num)) {
      lastEmittedRef.current = num
      onChange(num)
    }
  }

  /** Validates and applies digit-only text during typing. Returns false if rejected. */
  const applyRawInput = (raw: string): boolean => {
    if (isEffectivelyDisabled) return false
    if (raw === '') {
      setInputValue('')
      emit(safeMin)
      return true
    }
    if (!DIGITS_ONLY.test(raw)) return false
    setInputValue(raw)
    const parsed = Number(raw)
    if (Number.isInteger(parsed)) emit(parsed)
    return true
  }

  /** Finalizes value on blur/enter — floors to min, strips leading zeros. */
  const commitValue = () => {
    if (isEffectivelyDisabled) return
    const fallback = Math.max(safeMin, Number.isFinite(value) ? Math.trunc(value) : safeMin)
    const parsed = inputValue === '' ? (emptyWhenZero ? safeMin : fallback) : Number(inputValue)
    const final = Number.isInteger(parsed) ? Math.max(safeMin, parsed) : fallback
    setInputValue(convertToDisplayLabel(final, safeMin, emptyWhenZero))
    emit(final)
  }

  const isEmpty = value <= 0 && !isFocused
  const isOosClickable = isOutOfStock && !!onOosClick
  const handleOosActivate = () => {
    if (!isOosClickable) return
    onOosClick()
  }

  return (
    <div
      className={classNames(
        styles.wrapper,
        isOutOfStock && styles.wrapperOos,
        isOosClickable && styles.wrapperOosClickable,
        isOosActive && styles.wrapperOosActive,
        className,
      )}
      onClick={isOosClickable ? handleOosActivate : undefined}
      role={isOosClickable ? 'button' : undefined}
      tabIndex={isOosClickable ? 0 : undefined}
      aria-label={isOosClickable ? t('out_of_stock') : undefined}
      onBlur={isOosClickable ? onOosBlur : undefined}
      onKeyDown={
        isOosClickable
          ? (e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleOosActivate()
              }
            }
          : undefined
      }
    >
      {/* eslint-disable i18next/no-literal-string */}
      <input
        name="numeric-qty"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={inputValue}
        disabled={isEffectivelyDisabled}
        aria-label={ariaLabel}
        className={classNames(
          styles.input,
          variant === 'grid' && styles.grid,
          isEffectivelyDisabled && styles.disabled,
          isOutOfStock && styles.outOfStock,
          !isOutOfStock && isEmpty && styles.empty,
          derivedHasError && !isOutOfStock && styles.error,
        )}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          if (isComposing) {
            setInputValue(event.target.value)
            return
          }
          applyRawInput(event.target.value)
        }}
        onFocus={(_e: FocusEvent<HTMLInputElement>) => {
          setIsFocused(true)
        }}
        onBlur={(_e: FocusEvent<HTMLInputElement>) => {
          setIsFocused(false)
          if (!isComposing) commitValue()
        }}
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
          if (isEffectivelyDisabled || event.key !== 'Enter') return
          event.preventDefault()
          commitValue()
          event.currentTarget.blur()
        }}
        onPaste={(event: ClipboardEvent<HTMLInputElement>) => {
          event.preventDefault()
          if (isEffectivelyDisabled) return
          const cleaned = event.clipboardData.getData('text').replace(WHITESPACE, '')
          if (cleaned && DIGITS_ONLY.test(cleaned)) applyRawInput(cleaned)
        }}
        onCompositionStart={(_e: CompositionEvent<HTMLInputElement>) => {
          setIsComposing(true)
        }}
        onCompositionEnd={(event: CompositionEvent<HTMLInputElement>) => {
          setIsComposing(false)
          if (!applyRawInput(event.currentTarget.value))
            setInputValue(convertToDisplayLabel(value, safeMin, emptyWhenZero))
        }}
      />
      {/* eslint-enable i18next/no-literal-string */}
    </div>
  )
}
